import { HStack, VStack, Spinner, Center } from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import {
  getHistory,
  getHistoryById,
} from "../../../../../../utils/api/history.api";
import {
  useHistoryDispatch,
  useHistoryState,
} from "../../../../../../utils/context/HistoryContext";
import { useUserInfo } from "../../../../../../utils/hooks/useUserInfo";
import { CallHistory } from "../../../../../../utils/types";
import SlideRange from "../../../../../common/SlideRange";
import Export from "../Export";
import NoItem from "../NoItem";
import SingleCallCard from "./SingleCallCard";
import { useLocation } from "react-router-dom";

type CallsType = {
  setClickedRecord: Dispatch<SetStateAction<any>>;
  clickedRow: any;
};

function Calls({ setClickedRecord, clickedRow }: CallsType) {
  const dealership = useUserInfo("dealership");
  const [callActivities, setCallActivities] = useState<CallHistory[]>([]);
  const [sliderValue, setSliderValue] = useState("30");
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const { shouldRefetch } = useHistoryState();
  const dispatch = useHistoryDispatch();

  const location = useLocation();
  const recordId = location?.state?.call_id;

  const { isLoading, refetch, isFetching } = useQuery({
    queryKey: ["callHistory", clickedRow?.id, sliderValue, page],
    queryFn: () =>
      getHistory({
        dealershipId: dealership?.id,
        contactId: clickedRow?.id,
        type: "CallHistory",
        days: sliderValue,
        page,
      }),
    retry: false,
    onSuccess: (data) => {
      if (callActivities[0]?.contact_id !== clickedRow?.id) {
        setCallActivities(data.data.histories || []);
        setPage(1);
      } else {
        const filteredNewHistories = data?.data?.histories?.filter(
          (newHistory: any) =>
            !callActivities?.some((existing) => existing.id === newHistory.id)
        );

        setCallActivities((prev) => [...prev, ...filteredNewHistories]);
      }
      setTotalCount(data.data.histories_count);
    },
  });

  useQuery({
    queryKey: ["callHistoryById", clickedRow?.id, recordId],
    queryFn: () =>
      getHistoryById({
        dealershipId: dealership?.id,
        contactId: clickedRow?.id,
        id: recordId,
      }),
    retry: false,
    onSuccess: (data) => {
      setClickedRecord(data?.data?.history);
    },
  });

  useEffect(() => {
    setPage(1);
    setCallActivities([]);
    refetch();
  }, [clickedRow?.id, refetch, sliderValue]);

  useEffect(() => {
    if (shouldRefetch) {
      setPage(1);
      setCallActivities([]);
      refetch();
    }

    dispatch({ type: "SET_SHOULD_REFETCH", payload: { shouldRefetch: false } });
  }, [dispatch, refetch, shouldRefetch]);

  return (
    <>
      {" "}
      <Export type="CallHistory" days={sliderValue} id={clickedRow?.id} />
      <HStack background="var(--grey-200)" padding="1rem" w="100%">
        <SlideRange handleClick={(value) => setSliderValue(value)} />
      </HStack>{" "}
      {isFetching && page === 1 && (
        <Center w="100%" h="calc(100vh - 375px)">
          <Spinner />
        </Center>
      )}{" "}
      {!!callActivities.length &&
      !!callActivities.filter((item) => !!item.details).length ? (
        <div
          id="scrollableDivCall"
          style={{ height: "calc(100vh - 375px)", overflow: "auto" }}
        >
          <InfiniteScroll
            dataLength={callActivities.length}
            next={() => {
              setPage((prev) => prev + 1);
            }}
            hasMore={callActivities.length < totalCount}
            loader={<h4>Loading...</h4>}
            scrollableTarget="scrollableDivCall"
          >
            <VStack
              alignItems="flex-start"
              gap="1rem"
              padding="1rem"
              background="var(--grey-200)"
            >
              {callActivities.map((item) => {
                return (
                  <SingleCallCard
                    item={item}
                    setClickedRecord={setClickedRecord}
                  />
                );
              })}
            </VStack>
          </InfiniteScroll>
        </div>
      ) : (
        !isLoading && !isFetching && <NoItem />
      )}
    </>
  );
}

export default Calls;
